
// @ts-ignore
import {translate} from '@elements/translations';
import {ref, watch, toValue, computed, shallowReadonly, isProxy, unref, toRaw, reactive, onMounted, defineComponent} from 'vue';
import Breadcrumb from "../../components/breadcrumb/Breadcrumb.vue";
import {useTicketStore} from '../../stores/TicketStore';
import {storeToRefs} from 'pinia'
import LoadingSpinner from "../../components/loading/LoadingSpinner.vue";
import {
    areEqualArrays,
    throwAlertMessage,
    convertInCurrency
} from "../../utils/utils";
import {useLoginStore} from "../../stores/LoginStore.ts";
import EditModal from "../../components/modal/EditModal.vue";
// @ts-ignore
import Popper from 'vue3-popper/dist/popper.esm';
import TicketConfigurations from "../../components/configurations/TicketConfigurations.vue";

export default defineComponent({
    name: 'SummaryView',
    components: {
        TicketConfigurations,
        Breadcrumb,
        LoadingSpinner,
        EditModal,
        Popper
    },
    props: {
        title: {
            type: String,
            required: true,
        },
        ticketId: {
            type: String,
            required: true,
        },
        stepViews: {
            type: Array,
            required: true
        },
        hasServiceView: {
            type: Boolean,
            required: true
        },
        isPriceCalculator: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    setup(props) {
        const ticketStore = useTicketStore();

        const {
            step,
            selectedDateStart,
            selectedDateEnd,
            selectedOption,
            selectedInsurance,
            selectedUpgrades,
            selectedPriceGroup,
            selectedCurrency,
            selectedPriceConfiguration,
            allPriceGroups,
            allInsurances,
            allUpgrades,
            allOptions,
            ticketConfigurations,
        } = storeToRefs(ticketStore);

        const {
            changeStep,
            changeConfigurations,
            changeFinalPrice,
            changeTicketConfigurations,
        } = ticketStore;

        const loginStore = useLoginStore();
        const {profilePersons} = storeToRefs(loginStore);

        const isLoading = ref<boolean>(true);
        const priceGroups = ref<object>([]);
        const priceConfigurations = ref<object>([]);
        const showEditModal = ref<boolean>(false);
        const editConfigurationItem = ref<object>(null);
        const finalConfigurations = ref<object>([]);
        const priceString = ref<String>(null);
        const editPriceConfiguration = ref<object>(null);
        const editTempConfiguration = ref<object>(null);
        let initialConfigurations = ref<object>([]);

        const onChangeStep = (step: number) => {
            changeStep(step);
        }

        const onChangePrice = (price: number) => {
            priceString.value = convertInCurrency(price, selectedCurrency.value) + " " + selectedCurrency.value;
        }

        const onChangePriceConfiguration = (priceConfiguration: object) => {
            if (priceConfiguration.priceByPriceGroup) {
                priceConfiguration.priceByPriceGroup.forEach((item) => {
                    if (item.id === editConfigurationItem.value.priceGroup) {
                        editConfigurationItem.value['price'] = item;
                        editConfigurationItem.value['mappedPriceGroup'] = getMappedPriceGroup(item.id, priceConfiguration.priceGroupMap);
                    }
                });
            }

            editPriceConfiguration.value = priceConfiguration;
            editConfigurationItem.value['upgradeCatalog'] = editPriceConfiguration.value.upgradeCatalog;
            editConfigurationItem.value['upgradeOption'] = editPriceConfiguration.value.upgradeOption;
        }

        const onChangeTicketConfigurations = (ticketConfig: object) => {
            console.log("change ticketconfig", ticketConfig);
        }

        const deleteConfiguration = (id: string) => {
            let tempConfiguration = finalConfigurations.value.filter((item) => {
                return item.id !== id;
            });

            let configToDelete = finalConfigurations.value.filter((item) => {
                return item.id === id;
            })[0]

            let ticketConfig = {...ticketConfigurations.value};
            let priceGroup = configToDelete.priceGroup;
            let person = configToDelete.personId;

            if (person) {
                ticketConfig['persons'][person].counter = 0;
            } else {
                ticketConfig['priceGroups'][priceGroup].counter = 0;
            }

            changeTicketConfigurations(ticketConfig);
            finalConfigurations.value = tempConfiguration;
            changeConfigurations(tempConfiguration);
            calculatePrice();

            //if there are no more configurations, go back to the previous step
            if (Object.keys(finalConfigurations.value).length === 0) {
                changeStep(+step.value - 1);
            }
        }

        const editConfiguration = (id: string) => {
            //open a modal with the configuration to edit
            showEditModal.value = true;
            initialConfigurations.value = [];
            finalConfigurations.value.forEach((item) => {
                initialConfigurations.value.push({...item});
            });

            let temp = getFinalConfigurationItem(id);
            editConfigurationItem.value = toRaw(temp);


            //destruction fix => otherwise initialConfigurations.value + price + upgrades will be a reference to the finalConfigurations.value
            initialConfigurations.value.forEach((item) => {
                if (item.upgrades) {
                    item.upgrades = [...item.upgrades];
                }
                if (item.price) {
                    item.price = {...item.price};
                }
            });
        }

        const closeAndResetEditModal = () => {
            closeEditModal();
            changeConfigurations([...initialConfigurations.value ]);
            editTempConfiguration.value = {...ticketConfigurations.value};
            editConfigurationItem.value = {};
            editPriceConfiguration.value = {...selectedPriceConfiguration.value};

        }

        const closeEditModal = () => {
            showEditModal.value = false;
            editConfigurationItem.value = {};
            document.body.classList.remove("body--edit-modal-open");
        }

        const getPriceGroupItem = (id: string) => {
            let priceGroupItem = null;
            allPriceGroups.value.forEach((item) => {

                if (item.value === id) {
                    priceGroupItem = item;
                }
            });
            return priceGroupItem;
        }

        const getPersonItem = (id: string) => {
            let personItem = null;
            profilePersons.value.forEach((item) => {
                if (item.id === id) {
                    personItem = item;
                }
            });
            return personItem;
        }

        const getInsuranceItem = (id: string) => {
            let insuranceItem = null;
            allInsurances.value.forEach((item) => {
                if (item.id === id) {
                    insuranceItem = item;
                }
            });
            return insuranceItem;
        }

        const getUpgradeItems = (upgrades: string[]) => {
            let upgradeItems = [];
            allUpgrades.value.forEach((item) => {
                if (upgrades.includes(item.id)) {
                    upgradeItems.push(item);
                }
            });
            return upgradeItems;
        }

        const getOptionItem = (id: string) => {
            let optionItem = null;
            allOptions.value.forEach((item) => {
                if (item.id === id) {
                    optionItem = item;
                }
            });
            return optionItem;
        }

        const onChangeOption = (option: string) => {
            editConfigurationItem.value['option'] = option;

        }

        const onChangeInsurance = (insurance: string) => {
            editConfigurationItem.value['insurance'] = insurance;
        }

        const onChangeUpgrades = (upgrades: string[]) => {
            editConfigurationItem.value['upgrades'] = [];
            editConfigurationItem.value['upgrades'] = [...upgrades];
        }

        const getMappedPriceGroup = (priceGroup, priceConfiguration = selectedPriceConfiguration.value.priceGroupMap) => {
            let mappedPriceGroup = null;
            try {
                mappedPriceGroup = priceConfiguration[priceGroup];
            } catch (e) {
                console.warn("priceGroup " + priceGroup + "not found in priceGroupMap " + priceConfiguration + "!" + e);
            }
            return mappedPriceGroup;
        }

        const getConfigurations = () => {
            let ticketConfig = toRaw(ticketConfigurations.value);
            let finalConfig = [];
            Object.keys(ticketConfig['persons']).map((item, key) => {
                let person = toRaw(ticketConfig['persons'][item]);
                if (person.counter > 0) {
                    finalConfig.push({
                        id: 'configuration-' + key,
                        personId: item,
                        priceGroup: person.priceGroup,
                        mappedPriceGroup: getMappedPriceGroup(person.priceGroup),
                        price: {...person.price},
                        dateStart: toRaw(selectedDateStart.value),
                        dateEnd: toRaw(selectedDateEnd.value),
                        option: toRaw(selectedOption.value),
                        insurance: toRaw(selectedInsurance.value),
                        upgrades: [...selectedUpgrades.value],
                        upgradeOption: toRaw(selectedPriceConfiguration.value.upgradeOption),
                        upgradeCatalog: toRaw(selectedPriceConfiguration.value.upgradeCatalog),
                    });
                }
            });

            Object.keys(ticketConfig['priceGroups']).map((item, key) => {
                let priceGroup = toRaw(ticketConfig['priceGroups'][item]);
                let personsLength = Object.keys(ticketConfig['persons']).length;
                let configurationKey = 'configuration-' + (+personsLength + +key);
                if (priceGroup.counter > 0) {
                    for (let i = 0; i < priceGroup.counter; i++) {
                        finalConfig.push({
                            id: configurationKey + '-' + i,
                            personId: null,
                            priceGroup: item,
                            mappedPriceGroup: getMappedPriceGroup(item),
                            price: {...priceGroup.price},
                            dateStart: toRaw(selectedDateStart.value),
                            dateEnd: toRaw(selectedDateEnd.value),
                            option: toRaw(selectedOption.value),
                            insurance: toRaw(selectedInsurance.value),
                            upgrades: [...selectedUpgrades.value],
                            upgradeOption: toRaw(selectedPriceConfiguration.value.upgradeOption),
                            upgradeCatalog: toRaw(selectedPriceConfiguration.value.upgradeCatalog)
                        });
                    }
                }
            });
            
            changeConfigurations(toRaw(finalConfig));
            finalConfigurations.value = finalConfig;
        }

        const getFinalConfigurationItem = (id: string) => {
            let finalConfigurationItem = null;

            [...finalConfigurations.value].forEach((item) => {
                if (item.id === id) {
                    finalConfigurationItem = toRaw(item);
                }
            });
            return finalConfigurationItem;
        }

        const saveConfiguration = (configuration: object) => {
            let finalConfig = finalConfigurations.value;
            // find the configuration to update
            finalConfig.forEach((item, key) => {
                if (item.id === configuration['id']) {
                    finalConfig[key] = configuration;
                }
            });
            closeEditModal();
            calculatePrice();
            changeConfigurations(finalConfig);
        }

        const calculatePrice = () => {
            let finalConfig = ticketConfigurations.value.configurations;
            let total = 0;

            finalConfig.forEach((item, key) => {
                if (isProxy(item.price.price)) {
                    total += +toRaw(item.price.price);
                } else {
                    total += +item.price.price;
                }

            });

            changeFinalPrice(total);
            return total;
        }


        onMounted(() => {
            getConfigurations();
            calculatePrice();
            editPriceConfiguration.value = {...selectedPriceConfiguration.value};
            editTempConfiguration.value = {...ticketConfigurations.value};

        });

        return {
            step,
            selectedDateStart,
            selectedDateEnd,
            selectedOption,
            selectedInsurance,
            changeStep,
            onChangeStep,
            priceGroups,
            priceConfigurations,
            selectedPriceGroup,
            editPriceConfiguration,
            selectedPriceConfiguration,
            allPriceGroups,
            ticketConfigurations,
            selectedCurrency,
            translate,
            deleteConfiguration,
            profilePersons,
            editConfiguration,
            showEditModal,
            closeEditModal,
            getPriceGroupItem,
            getPersonItem,
            getConfigurations,
            convertInCurrency,
            getInsuranceItem,
            getUpgradeItems,
            getOptionItem,
            onChangeOption,
            onChangeInsurance,
            onChangeUpgrades,
            priceString,
            saveConfiguration,
            editConfigurationItem,
            onChangePriceConfiguration,
            onChangePrice,
            onChangeTicketConfigurations,
            finalConfigurations,
            editTempConfiguration,
            closeAndResetEditModal,
            initialConfigurations,
            getFinalConfigurationItem
        };
    }
});
